import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft, IconAlertTriangle, IconCheck,
  IconUser, IconClock, IconMapPin, IconTractor,
  IconPhone, IconDownload, IconBriefcase,
} from '@tabler/icons-react';

export default function IncidentDetail() {
  const { language } = useTranslation();
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile, role } = useAuth();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolution, setResolution] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !incidentId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId, 'incidents', incidentId));
        if (snap.exists()) setIncident({ id: snap.id, ...snap.data() });
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId, incidentId]);

  const handleResolve = async () => {
    if (!resolution.trim()) return;
    setResolving(true);
    try {
      await updateDoc(doc(db, 'organisations', orgId, 'incidents', incidentId), {
        status: 'resolved',
        resolution: resolution.trim(),
        resolvedBy: userProfile?.name,
        resolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setIncident(prev => ({ ...prev, status: 'resolved', resolution: resolution.trim(), resolvedBy: userProfile?.name }));
      setShowResolveModal(false);
      toast.success('Incident resolved');
    } catch (err) { toast.error('Failed to resolve incident'); }
    setResolving(false);
  };

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString(language, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const triggerLabel = (type) => {
    if (type === 'impact') return 'Impact detected';
    if (type === 'manual') return 'Manual SOS triggered';
    if (type === 'man_down') return 'Man-down alert';
    if (type === 'no_movement') return 'No movement detected';
    return type || '—';
  };

  if (loading) return <Layout><div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div></Layout>;

  if (!incident) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/incidents')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Incidents
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Incident not found</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>

        <div onClick={() => navigate('/incidents')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Incidents
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h1 style={{ fontSize: 20, fontWeight: 400 }}>Emergency incident</h1>
              <StatusPill status={incident.status || 'open'} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {triggerLabel(incident.triggerType)} · {formatDateTime(incident.detectedAt || incident.triggeredAt)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" icon={<IconDownload size={13} stroke={1.5} />} onClick={() => toast.info('HSE export coming soon')}>Export HSE</Button>
            {incident.status !== 'resolved' && (role === 'org_admin' || role === 'manager') && (
              <Button variant="success" size="sm" icon={<IconCheck size={13} stroke={1.5} />} onClick={() => setShowResolveModal(true)}>Resolve</Button>
            )}
          </div>
        </div>

        {incident.status === 'active' && (
          <div style={{ background: 'var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={18} color="#fff" stroke={1.5} />
            <div style={{ fontSize: 13, color: '#fff' }}>This emergency is still active — check on the operator immediately</div>
          </div>
        )}

        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Incident details</div>
          {[
            { icon: IconUser,          label: 'Operator',     value: incident.triggeredByName || '—' },
            { icon: IconClock,         label: 'Time',         value: formatDateTime(incident.detectedAt || incident.triggeredAt) },
            { icon: IconAlertTriangle, label: 'Trigger',      value: triggerLabel(incident.triggerType) },
            { icon: IconTractor,       label: 'Machine',      value: incident.machineName || '—' },
            { icon: IconAlertTriangle, label: 'Impact force', value: incident.impactForce ? incident.impactForce + 'G' : '—' },
          ].map(row => {
            const Icon = row.icon;
            return (
              <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <Icon size={14} color="var(--color-text-tertiary)" stroke={1.5} />
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', width: 110, flexShrink: 0 }}>{row.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{row.value}</span>
              </div>
            );
          })}
        </Card>

        {incident.call999FiredAt && (
          <Card style={{ marginBottom: 16, border: '0.5px solid var(--color-danger)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--color-danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconPhone size={16} color="var(--color-danger)" stroke={1.5} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-danger)', fontWeight: 400 }}>999 called automatically</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{formatDateTime(incident.call999FiredAt)}</div>
              </div>
            </div>
          </Card>
        )}

        {incident.responders?.length > 0 && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Responders</div>
            {incident.responders.map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < incident.responders.length - 1 ? '0.5px solid var(--color-border-light)' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconCheck size={14} color="var(--color-success)" stroke={2} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{r.name}</div>
                  {r.distanceMiles && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 1 }}>{r.distanceMiles} miles away</div>}
                </div>
                {r.respondedAt && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{new Date(r.respondedAt).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}</div>}
              </div>
            ))}
          </Card>
        )}

        {incident.location?.lat && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 10 }}>Location</div>
            <div
              onClick={() => window.open('https://www.google.com/maps?q=' + incident.location.lat + ',' + incident.location.lng, '_blank')}
              style={{ fontSize: 13, color: 'var(--color-brand)', display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
            >
              <IconMapPin size={14} stroke={1.5} />
              Open in Google Maps
            </div>
            {incident.location.what3words && (
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                {'///' + incident.location.what3words}
              </div>
            )}
          </Card>
        )}

        {incident.status === 'resolved' && (
          <Card style={{ border: '0.5px solid var(--color-success)' }}>
            <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-success)', marginBottom: 10 }}>Resolved</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.6, marginBottom: 8 }}>{incident.resolution}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Resolved by {incident.resolvedBy} · {formatDateTime(incident.resolvedAt)}</div>
          </Card>
        )}

      </div>

      <Modal open={showResolveModal} onClose={() => setShowResolveModal(false)} title="Resolve incident" size="sm" footer={
        <>
          <Button variant="secondary" onClick={() => setShowResolveModal(false)}>Cancel</Button>
          <Button variant="success" onClick={handleResolve} disabled={resolving || !resolution.trim()} loading={resolving} icon={<IconCheck size={14} stroke={1.5} />}>Mark as resolved</Button>
        </>
      }>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Resolution details <span style={{ color: 'var(--color-danger)' }}>*</span></label>
          <textarea placeholder="Describe the outcome and any actions taken..." value={resolution} onChange={e => setResolution(e.target.value)} rows={4} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} autoFocus />
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>This record will be permanently sealed and is exportable for HSE reporting.</div>
      </Modal>

    </Layout>
  );
}
